import React, { useState, useEffect } from 'react';
import { X, Shield } from 'lucide-react';
import { updateConsent } from '../services/privacyService';
import Button from './ui/Button';

/**
 * Cookie Consent Banner
 * GDPR/CCPA compliant cookie consent
 */
const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false
  });
  
  useEffect(() => {
    // Check if user has already consented
    const hasConsented = localStorage?.getItem('cookie_consent');
    if (!hasConsented) {
      setShowBanner(true);
    }
  }, []);
  
  const handleAcceptAll = async () => {
    const consentData = {
      essential: true,
      analytics: true,
      marketing: true
    };
    
    await saveConsents(consentData);
    setShowBanner(false);
  };
  
  const handleRejectAll = async () => {
    const consentData = {
      essential: true,
      analytics: false,
      marketing: false
    };
    
    await saveConsents(consentData);
    setShowBanner(false);
  };
  
  const handleSavePreferences = async () => {
    await saveConsents(preferences);
    setShowBanner(false);
    setShowPreferences(false);
  };
  
  const saveConsents = async (consentData) => {
    // Save to localStorage
    localStorage?.setItem('cookie_consent', JSON.stringify(consentData));
    localStorage?.setItem('cookie_consent_date', new Date()?.toISOString());
    
    // Save to database
    const consentVersion = '1.0';
    const consentText = 'User consented to cookie usage as per privacy policy';
    
    // Save each consent type
    if (consentData?.analytics) {
      await updateConsent({
        consentType: 'analytics',
        consentGiven: true,
        consentText,
        consentVersion
      });
    }
    
    if (consentData?.marketing) {
      await updateConsent({
        consentType: 'marketing',
        consentGiven: true,
        consentText,
        consentVersion
      });
    }
    
    // Essential cookies consent (always given)
    await updateConsent({
      consentType: 'essential',
      consentGiven: true,
      consentText: 'Essential cookies required for site functionality',
      consentVersion
    });
  };
  
  if (!showBanner) return null;
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t-2 border-gray-200 shadow-2xl">
      <div className="max-w-7xl mx-auto">
        {!showPreferences ? (
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Cookie Consent</h3>
              <p className="text-sm text-gray-600 mb-4">
                We use cookies to enhance your experience, analyze site traffic, and personalize content. 
                Essential cookies are required for basic site functionality. You can customize your preferences or accept all cookies.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleAcceptAll}
                  size="sm"
                >
                  Accept All Cookies
                </Button>
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  size="sm"
                >
                  Reject Non-Essential
                </Button>
                <Button
                  onClick={() => setShowPreferences(true)}
                  variant="outline"
                  size="sm"
                >
                  Customize Preferences
                </Button>
              </div>
            </div>
            <button
              onClick={() => setShowBanner(false)}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Cookie Preferences</h3>
                <p className="text-sm text-gray-600">
                  Manage your cookie preferences. Essential cookies cannot be disabled as they are required for the site to function.
                </p>
              </div>
              <button
                onClick={() => setShowPreferences(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4 mb-4">
              {/* Essential Cookies */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">Essential Cookies</h4>
                  <p className="text-sm text-gray-600">
                    Required for basic site functionality, authentication, and security. These cannot be disabled.
                  </p>
                </div>
                <div className="ml-4 text-sm text-gray-500 font-medium">Always Active</div>
              </div>
              
              {/* Analytics Cookies */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">Analytics Cookies</h4>
                  <p className="text-sm text-gray-600">
                    Help us understand how visitors interact with our website by collecting and reporting information anonymously.
                  </p>
                </div>
                <label className="ml-4 relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences?.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e?.target?.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              {/* Marketing Cookies */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">Marketing Cookies</h4>
                  <p className="text-sm text-gray-600">
                    Used to track visitors across websites to display relevant advertisements and measure campaign effectiveness.
                  </p>
                </div>
                <label className="ml-4 relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences?.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e?.target?.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleSavePreferences}
                size="sm"
              >
                Save Preferences
              </Button>
              <Button
                onClick={() => setShowPreferences(false)}
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;